import React, { useState, useEffect } from 'react';
import { useStore, put, remove, generateId } from '../hooks/useDB';
import PageLoader from '../components/PageLoader';
import { Plus, Trash2, Edit2, X, Map, MapPin, PhoneCall, Mail, Building, Inbox, Loader2 } from 'lucide-react';
import { toast } from '../utils/toast';
import { confirm } from '../utils/confirm';

const TYPES = ['hotel', 'restaurant', 'conference', 'activity', 'transport', 'other'];

function Modal({ venue, onSave, onClose }) {
  const [form, setForm] = useState(venue || {
    name: '', venueType: 'hotel', address: '', city: 'Hyderabad', capacity: 20,
    conferenceRoom: '', restaurantArea: '', mapLink: '',
    contactName: '', contactPhone: '', contactEmail: '',
    notes: '', status: 'shortlisted'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Venue name is required.'); return; }
    if (form.capacity && Number(form.capacity) < 1) { setError('Capacity must be at least 1.'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave(form);
    } catch {
      setError('Failed to save. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="tw-drawer-backdrop">
      <div className="tw-drawer-panel">
        {/* Header */}
        <div className="p-6 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{venue ? 'Modify Location Profile' : 'Add Location / Venue'}</h3>
            <p className="text-xs text-slate-500 mt-1">Configure meeting halls, maximum capacity, maps, and managers</p>
          </div>
          <button className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Venue Name *</label>
            <input 
              value={form.name} 
              onChange={(e) => set('name', e.target.value)} 
              placeholder="e.g. Hyatt Regency Beach Resort" 
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Type / Sector</label>
              <select 
                value={form.venueType} 
                onChange={(e) => set('venueType', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              >
                {TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Acquisition Status</label>
              <select 
                value={form.status} 
                onChange={(e) => set('status', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              >
                {['shortlisted', 'contacted', 'confirmed', 'rejected'].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Physical Address</label>
            <input 
              value={form.address} 
              onChange={(e) => set('address', e.target.value)} 
              placeholder="e.g. Candolim Beach Road, North Goa" 
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">City Location</label>
              <input 
                value={form.city} 
                onChange={(e) => set('city', e.target.value)} 
                placeholder="Goa" 
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Capacity headcount</label>
              <input 
                type="number" 
                value={form.capacity} 
                onChange={(e) => set('capacity', e.target.value)} 
                min={1} 
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider font-display">Accommodation / Rooms</label>
              <input 
                value={form.conferenceRoom || ''} 
                onChange={(e) => set('conferenceRoom', e.target.value)} 
                placeholder="Grand Horizon Ball Room" 
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outlook-none"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider font-display">Dining / banquet area</label>
              <input 
                value={form.restaurantArea || ''} 
                onChange={(e) => set('restaurantArea', e.target.value)} 
                placeholder="Rooftop deck / beachfront bar" 
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outlook-none"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Google Maps Link</label>
            <input 
              value={form.mapLink || ''} 
              onChange={(e) => set('mapLink', e.target.value)} 
              placeholder="https://maps.google.com/?q=..." 
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
            />
          </div>

          <div className="grid grid-cols-3 gap-3 bg-slate-50 border border-slate-150 p-4 rounded-xl">
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase">POC Manager</label>
              <input 
                value={form.contactName || ''} 
                onChange={(e) => set('contactName', e.target.value)} 
                placeholder="Sarah Lance" 
                className="w-full bg-white border border-slate-200 rounded px-2 py-1 text-xs"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase">POC Contact</label>
              <input 
                value={form.contactPhone || ''} 
                onChange={(e) => set('contactPhone', e.target.value)} 
                placeholder="+91-XXXXX" 
                className="w-full bg-white border border-slate-200 rounded px-2 py-1 text-xs"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase">POC Email</label>
              <input 
                value={form.contactEmail || ''} 
                onChange={(e) => set('contactEmail', e.target.value)} 
                placeholder="sarah@hyatt.com" 
                className="w-full bg-white border border-slate-200 rounded px-2 py-1 text-xs"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Internal Remarks</label>
            <textarea 
              value={form.notes} 
              onChange={(e) => set('notes', e.target.value)} 
              rows={2} 
              placeholder="Booking details, down-payment milestones, extra taxes..." 
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-100 bg-white">
          {error && <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
          <div className="flex gap-3 justify-end">
            <button className="px-4 py-2 text-sm font-semibold border border-slate-200 hover:bg-slate-50 rounded-lg transition" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button className="px-4 py-2 text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition flex items-center gap-2 disabled:opacity-60" onClick={handleSave} disabled={saving}>
              {saving && <Loader2 size={14} className="animate-spin" />}
              {venue ? 'Save Location' : 'Record Location'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const statusClass = { 
  shortlisted: 'sh-badge-warning', 
  contacted: 'sh-badge-info', 
  confirmed: 'sh-badge-success font-bold', 
  rejected: 'sh-badge-danger animate-none' 
};

export default function Venues() {
  const { data: venues, loading } = useStore('venues');
  const [modal, setModal] = useState(null);

  const save = async (form) => {
    await put('venues', { ...form, id: form.id || generateId() });
    setModal(null);
    window.dispatchEvent(new CustomEvent('planner:data-change'));
    toast.success(form.id ? 'Venue updated.' : 'Venue added.');
  };

  const del = async (id) => {
    const ok = await confirm('This will permanently delete this venue. Are you sure?', 'Delete Venue');
    if (ok) {
      try {
        await remove('venues', id);
        window.dispatchEvent(new CustomEvent('planner:data-change'));
        toast.success('Venue deleted.');
      } catch {
        toast.error('Failed to delete venue.');
      }
    }
  };

  const confirmedCount = venues.filter(v => v.status === 'confirmed').length;
  const contactedCount = venues.filter(v => v.status === 'contacted').length;
  const totalCapacity = venues.filter(v => v.status === 'confirmed').reduce((s, v) => s + (parseInt(v.capacity) || 0), 0);

  if (loading) return <PageLoader label="Loading venues…" sublabel="Fetching offsite spots and details" />;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold font-display text-slate-900 flex items-center gap-2">
            <Building style={{ color: 'var(--accent)' }} size={24} />
            <span>Offsite Venues & Spots</span>
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Maintain beachfront hostels, banquet dining coordinates, boardroom reservations, and manager hotlines
          </p>
        </div>
        <button 
          className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-semibold transition flex items-center gap-2 self-start sm:self-auto shadow-sm" 
          onClick={() => setModal('new')}
        >
          <Plus size={16} />
          <span>Add Offsite Venue</span>
        </button>
      </div>

      {/* Summary Matrix Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sh-stat-card border-l-4 border-emerald-500">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Confirmed Venues</span>
          </div>
          <div className="sh-stat-value text-slate-900">{confirmedCount}</div>
          <p className="text-xs text-slate-500">Acquisition contracts signed</p>
        </div>

        <div className="sh-stat-card">
          <div className="sh-stat-head">
            <span className="sh-stat-label">In Pipeline (Negotiation)</span>
          </div>
          <div className="sh-stat-value text-amber-600">{contactedCount + venues.filter(v => v.status === 'shortlisted').length}</div>
          <p className="text-xs text-slate-500">Shortlisted & contacted properties</p>
        </div>

        <div className="sh-stat-card border-l-4 border-slate-900">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Total Confirmed Capacity</span>
          </div>
          <div className="sh-stat-value text-slate-900">{totalCapacity} pax</div>
          <p className="text-xs text-slate-500">Acquisition boarding size limit</p>
        </div>
      </div>

      {/* Venue Lists */}
      {venues.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center shadow-sm">
          <Inbox className="mx-auto text-slate-300 mb-3" size={36} />
          <h3 className="text-base font-bold text-slate-800 font-display">Properties directory clear</h3>
          <p className="text-sm text-slate-500 mt-1">Add offsite accommodation venues, board rooms, and resort details here</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {venues.map((v) => (
            <div key={v.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4 hover:border-slate-300 transition duration-150 flex flex-col justify-between">
              <div className="space-y-3">
                <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                  <div>
                    <h3 className="text-base font-bold text-slate-900 tracking-tight">{v.name}</h3>
                    <span className="text-xs text-slate-500 font-medium capitalize mt-0.5 block">📍 {v.venueType || 'other'} {v.city ? `| ${v.city}` : ''}</span>
                  </div>
                  <span className={`sh-badge ${statusClass[v.status] || 'sh-badge-outline'}`}>{v.status}</span>
                </div>

                {v.address && (
                  <p className="text-xs text-slate-600 leading-normal flex items-start gap-1">
                    <MapPin size={12} className="text-slate-400 shrink-0 mt-0.5" />
                    <span>{v.address}</span>
                  </p>
                )}

                {/* Facilities */}
                {(v.conferenceRoom || v.restaurantArea || v.capacity) && (
                  <div className="grid grid-cols-2 gap-3 text-xs p-3 bg-slate-50/50 border border-slate-100 rounded-xl">
                    <div className="space-y-1">
                      <span className="text-slate-400 font-bold block uppercase tracking-wider text-[10px]">Halls & Conference</span>
                      <strong className="text-slate-850 font-display text-slate-800">{v.conferenceRoom || '—'}</strong>
                    </div>
                    <div className="space-y-1">
                      <span className="text-slate-400 font-bold block uppercase tracking-wider text-[10px]">Max Boarding Cap</span>
                      <strong className="text-slate-850 text-slate-800">{v.capacity || '20'} seats</strong>
                    </div>
                    {v.restaurantArea && (
                      <div className="col-span-2 space-y-1 pt-1.5 border-t border-dashed border-slate-150">
                        <span className="text-slate-400 font-bold block uppercase tracking-wider text-[10px]">Dining spaces</span>
                        <strong className="text-slate-850 text-slate-800">{v.restaurantArea}</strong>
                      </div>
                    )}
                  </div>
                )}

                {/* Manager POC contact panel */}
                {v.contactName && (
                  <div className="border border-slate-150 rounded-xl p-3 bg-slate-50 space-y-2">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">On-site Hotel POC</span>
                    <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                      <strong className="text-slate-800">{v.contactName}</strong>
                      <div className="flex items-center gap-1.5">
                        {v.contactPhone && (
                          <a 
                            href={`tel:${v.contactPhone}`} 
                            title="Call manager phone" 
                            className="p-1 px-2 border border-slate-205 bg-white text-slate-700 hover:text-slate-900 rounded-md shadow-xs flex items-center gap-1 font-semibold transition"
                          >
                            <PhoneCall size={10} />
                            <span>Dial</span>
                          </a>
                        )}
                        {v.contactEmail && (
                          <a 
                            href={`mailto:${v.contactEmail}`} 
                            title="Email property coordinator" 
                            className="p-1 px-2 border border-slate-205 bg-white text-slate-705 text-slate-700 hover:text-slate-900 rounded-md shadow-xs flex items-center gap-1 transition"
                          >
                            <Mail size={10} />
                            <span>Mail</span>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-4 mt-2">
                <div>
                  {v.mapLink ? (
                    <a 
                      href={v.mapLink} 
                      target="_blank" 
                      rel="noreferrer" 
                      className="inline-flex items-center gap-1 text-xs text-sky-700 hover:text-sky-900 font-semibold align-middle"
                    >
                      <Map size={12} />
                      <span>Google Map coordinates</span>
                    </a>
                  ) : (
                    <span className="text-xs text-slate-400 font-medium">No Map linked</span>
                  )}
                </div>

                <div className="flex items-center gap-1">
                  <button 
                    className="p-1.5 text-slate-450 text-slate-500 hover:text-slate-900 hover:bg-slate-50 border border-transparent hover:border-slate-200 rounded-lg transition" 
                    onClick={() => setModal(v)}
                  >
                    <Edit2 size={13} />
                  </button>
                  <button 
                    className="p-1.5 text-red-400 hover:text-red-700 hover:bg-red-50 border border-transparent hover:border-red-100 rounded-lg transition" 
                    onClick={() => del(v.id)}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && <Modal venue={modal === 'new' ? null : modal} onSave={save} onClose={() => setModal(null)} />}
    </div>
  );
}
